import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

// #241: rotating beta-status messages, replacing the static "All rights
// reserved" copyright tail. Picked once per session (not per page load) so
// it doesn't visibly flicker between different text as you navigate - still
// varies across visits/tabs. Easy to remove later once mobile is polished:
// delete this array and revert the render below to static text.
const FOOTER_MESSAGES = [
  { tag: 'Still Raw in Places', desktopNote: 'Best on desktop (for now)' },
  { tag: 'Beta AF', desktopNote: 'Desktop strongly recommended' },
  { tag: 'Beta Like Your Diet', desktopNote: 'Works best on desktop' },
];

function pickFooterMessage() {
  try {
    const stored = sessionStorage.getItem('footerMessageIndex');
    const storedIndex = stored !== null ? Number(stored) : NaN;
    if (FOOTER_MESSAGES[storedIndex]) {
      return FOOTER_MESSAGES[storedIndex];
    }
    const index = Math.floor(Math.random() * FOOTER_MESSAGES.length);
    sessionStorage.setItem('footerMessageIndex', String(index));
    return FOOTER_MESSAGES[index];
  } catch {
    // Storage unavailable (e.g. private browsing) - still show a message,
    // just without cross-page consistency.
    return FOOTER_MESSAGES[Math.floor(Math.random() * FOOTER_MESSAGES.length)];
  }
}

const Footer = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [footerMessage] = useState(pickFooterMessage);
  const footerRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile the footer is position:fixed (see Footer.css), which takes it
  // out of normal document flow - nothing reserves space for it, so it sits
  // on top of whatever content happens to be scrolled to the bottom of any
  // page. Measure the footer's own actual rendered height (it varies with
  // the expand/collapse toggle and the rotating message length) and publish
  // it as a CSS variable so page layouts can reserve matching bottom padding,
  // instead of every page guessing a hardcoded pixel value that drifts out
  // of sync the next time this component's content changes.
  useEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl) return undefined;

    const updateHeightVar = () => {
      document.documentElement.style.setProperty(
        '--mobile-footer-height',
        `${footerEl.offsetHeight}px`
      );
    };

    updateHeightVar();
    const observer = new ResizeObserver(updateHeightVar);
    observer.observe(footerEl);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--mobile-footer-height');
    };
  }, []);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <footer ref={footerRef} className={`site-footer ${isMobile && isExpanded ? 'expanded' : ''}`}>
      {isMobile && (
        <div className="footer-toggle" onClick={toggleExpanded}>
          <span className="footer-toggle-text">
            © {new Date().getFullYear()} Carnivores Adapting Terribly
          </span>
          <span className="footer-toggle-icon">{isExpanded ? '▼' : '▲'}</span>
        </div>
      )}

      <div className={`footer-content ${isMobile && !isExpanded ? 'hidden' : ''}`}>
        <Link to="/" className="footer-link">
          Home
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/contact" className="footer-link">
          Contact/Bitch/Whine
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/terms" className="footer-link">
          Terms of Service
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/privacy" className="footer-link">
          Privacy Policy
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/faq" className="footer-link">
          FAQ
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/guidelines" className="footer-link">
          Guidelines
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/about" className="footer-link">
          About
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/deck-builder" className="footer-link">
          😏 Deck Around
        </Link>
        <span className="footer-separator">|</span>
        <Link to="/steak" className="footer-link footer-support">
          🥩 Buy Me A Steak
        </Link>
      </div>

      {(!isMobile || isExpanded) && (
        <div className="footer-copyright">
          © {new Date().getFullYear()} Carnivores Adapting Terribly ({footerMessage.tag})
          <div className="footer-note">{footerMessage.desktopNote}</div>
          {process.env.REACT_APP_GIT_SHA && (
            <div className="footer-build-info">
              Build {process.env.REACT_APP_GIT_SHA.slice(0, 7)}
              {process.env.REACT_APP_BUILD_TIME &&
                ` · Deployed ${new Date(Number(process.env.REACT_APP_BUILD_TIME) * 1000).toLocaleString()}`}
            </div>
          )}
        </div>
      )}
    </footer>
  );
};

export default Footer;
